import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

import type {
  NavigatorFilters,
  NavigatorLead,
  SelectedNavigatorFilters,
} from "../types";

interface PagingParams {
  pageSize: number;
  page: number;
}

async function getLeadsRequest(
  workspaceId: string,
  accountId: string,
  selectedFilters: SelectedNavigatorFilters,
  keywords: string,
  pagingParams: PagingParams,
): Promise<{
  filters: NavigatorFilters;
  leads: NavigatorLead[];
  totalCount: number;
  isLimitExceeded?: boolean;
}> {
  const { page, pageSize } = pagingParams;
  const queryParams = new URLSearchParams();
  queryParams.set("start", (page * pageSize).toString());
  queryParams.set("count", pageSize.toString());

  const {
    filters,
    people,
    paging,
    limit_exceeded: isLimitExceeded,
  } = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/sales-navigator/search?${queryParams.toString()}`,
    {
      filters: selectedFilters,
      keywords: keywords || undefined,
    },
  );
  return { filters, leads: people, totalCount: paging.total, isLimitExceeded };
}

export default function useNavigatorLeads(
  {
    accountId,
    selectedFilters,
    keywords,
  }: {
    accountId: string;
    selectedFilters: SelectedNavigatorFilters;
    keywords: string;
  },
  pagingParams: PagingParams,
) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { leadListSourcesKeys } = getQueryKeys(workspaceId);

  const {
    data,
    isFetching: isFetchingSalesNavigatorLeads,
    error: navigatorLeadsError,
    refetch: refetchNavigatorLeads,
  } = useQuery({
    queryKey: leadListSourcesKeys.navigatorLeads(
      accountId,
      keywords,
      selectedFilters,
      pagingParams,
    ),
    enabled: !!accountId && (!!selectedFilters.length || !!keywords),
    placeholderData: (previousData, previousQuery) => {
      if (previousData && previousQuery) {
        const previousSelectedFilters = previousQuery.queryKey[5];
        const previousKeywords = previousQuery.queryKey[4];

        if (
          (selectedFilters.length &&
            previousSelectedFilters === selectedFilters) ||
          (!!keywords && previousKeywords === keywords)
        ) {
          return previousData;
        }
      }
    },
    queryFn: () =>
      getLeadsRequest(
        workspaceId,
        accountId,
        selectedFilters,
        keywords,
        pagingParams,
      ),
    gcTime: 0,
  });

  return {
    isFetchingSalesNavigatorLeads,
    filters: data?.filters,
    leads: data?.leads,
    totalCount: data?.totalCount,
    isLimitExceeded: data?.isLimitExceeded,
    navigatorLeadsError,
    refetchNavigatorLeads,
  };
}
